import React, { useState } from 'react';
import Modal from '../components/Modal';
import IdentityCard from '../components/IdentityCard';

const Projects: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleProjectClick = (projectId: string) => {
    setSelectedProject(projectId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };
  const projects = [
    {
      id: 'tripbot',
      title: 'TripBot: Conversational Interface for BLV Travel Assistance',
      tags: ['Human-Computer Interaction', 'Conversational AI', 'Large Language Models', 'User Research', 'Assistive Technology'],
      timeline: 'January 2024 - Present',
      description: [
        'The development of TripBot began with identifying a critical gap in existing travel assistance solutions for Blind and Low Vision (BLV) individuals.',
        'While current solutions provided basic navigation assistance, they often failed to address the need for real-time environmental awareness and contextual information.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <h3 class="modal-section-title">Research Context</h3>
            <div class="modal-content-block">
              <p class="modal-text">The development of TripBot began with identifying a critical gap in existing travel assistance solutions for Blind and Low Vision (BLV) individuals. While current solutions provided basic navigation assistance, they often failed to address the need for real-time environmental awareness and contextual information.</p>
              <p class="modal-text">This limitation became particularly apparent during initial auto-ethnography conducted with a BLV team member, who expressed frustration with systems that couldn't adapt to dynamic urban environments or provide meaningful context about their surroundings.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Process & Design Decisions</h3>
            <p class="modal-intro-text">Through our auto-ethnography, I derived key insights:</p>
            
            <div class="insight-card">
              <h4 class="insight-title">1. Environmental Context Needs</h4>
              <ul class="insight-list">
                <li>BLV individuals want to understand not just their stand-alone location, but the surrounding context.</li>
                <li>There is no standardized resource for BLV people to inquire about cane tips and contextualized and accurate travel guidance based on exact times of travel.</li>
                <li>BLV individuals wish to gain information about potential obstacles or changes in the environment based on their plans and want to be prepared for their trip in advance.</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">2. Technical Approach Selection</h4>
              <p class="modal-text">Based on these findings, we chose to develop a conversational interface for several reasons:</p>
              <ul class="insight-list">
                <li>Natural language allows for more nuanced communication of environmental context.</li>
                <li>Conversational interfaces can adapt to different levels of detail based on user needs and is the primary mode of learning for BLV people.</li>
                <li>The format enables two-way communication, allowing users to ask for specific information and providing a channel for relaying accurate feedback.</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">3. LLM Integration Decision</h4>
              <p class="modal-text">The Initial testing with GPT-3.5 showed limitations in understanding spatial relationships and answers riddled with hallucinations. I therefore prompt engineered and developed a Retrieval-Augmented Generation pipeline for leveraging LLaMA-2's contextual capabilities allowed for better processing of environmental context.</p>
              <p class="modal-text">I was able to generate reduced hallucinations through inputting weather data directly via the OpenWeatherMap API and Orientation and Mobility (O & M) assistive technology dataset (which was curated by a team member). However, we still faced certain devolving conversations where the LLM could not remember context.</p>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">4. Findings</h4>
              <p class="modal-text">The internal system testing indicated varying preferences for detail levels where team members wished to customize the verbosity of the LLM. Another theme of the need to balance information density with cognitive load emerged, which highlighted the importance of personalizing the experience for different users.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Implementation Challenges & Solutions</h3>
            <p class="modal-intro-text">During the implementation phase, we encountered several challenges:</p>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Environmental Understanding</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Challenge:</span>
                  <p class="challenge-text">Accurately interpreting information about complex urban environments the user wants to traverse.</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Solution:</span>
                  <p class="challenge-text">Developed an approach of feeding data for RAG in which accurate data was dynamically fetched and fed into the LLM.</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Result:</span>
                  <p class="challenge-text">Improved accuracy in identifying and describing environmental features.</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">Response Generation</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Challenge:</span>
                  <p class="challenge-text">Creating natural, informative responses without overwhelming users.</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Solution:</span>
                  <p class="challenge-text">Implemented a context-aware response system that considers:</p>
                  <ul class="nested-list">
                    <li>User's current activity</li>
                    <li>Environmental complexity</li>
                    <li>User's stated preferences</li>
                  </ul>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Result:</span>
                  <p class="challenge-text">More natural and useful responses that adapt to the situation that allows users to initiate and direct conversation.</p>
                </div>
              </div>
            </div>
            
            <div class="iteration-timeline">
              <h4 class="iteration-title">User Testing & Iteration</h4>
              <p class="modal-text">The system underwent three major iterations based on user feedback:</p>
              <div class="timeline-items">
                <div class="timeline-item">
                  <span class="timeline-number">1</span>
                  <p class="timeline-text">Focused on basic navigation and environmental description.</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">2</span>
                  <p class="timeline-text">Added preference-based customization.</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">3</span>
                  <p class="timeline-text">Implemented continuous learning from user interactions (text-based).</p>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Current Status & Future Directions</h3>
            <p class="modal-intro-text">The project is currently in the pilot testing phase, with several key areas of focus:</p>
            
            <div class="status-grid">
              <div class="status-card">
                <h4 class="status-title">System Performance</h4>
                <ul class="status-list">
                  <li>Evaluating response accuracy in various urban environments.</li>
                  <li>Implementing a fine-tuned LLaVA model to provide a platform for multimodal streaming.</li>
                  <li>Measuring user satisfaction with different types of information by analyzing user responses through vanilla sentiment analysis and NLU algorithms for tone analysis and topic modelling.</li>
                  <li>Assessing the effectiveness of the system through transformer-based benchmarking.</li>
                </ul>
              </div>
              
              <div class="status-card">
                <h4 class="status-title">User Experience</h4>
                <ul class="status-list">
                  <li>Gathering feedback on the conversational interface.</li>
                  <li>Identifying areas for improvement in response generation.</li>
                  <li>Understanding how users adapt to the system over time. I wish to understand if system familiarity should be considered as an independent variable.</li>
                </ul>
              </div>
            </div>
          </section>
          
          <div class="modal-footer">
            <a href="/projects" class="btn btn-secondary">Back to Projects</a>
          </div>
        </div>
        
        <style>
          .project-modal-content {
            max-width: 100%;
            line-height: 1.6;
          }
          
          .modal-section {
            margin-bottom: 2.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .modal-section:last-of-type {
            border-bottom: none;
          }
          
          .modal-section-title {
            color: #1f2937;
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .modal-content-block {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 8px;
            border-left: 4px solid #000000;
          }
          
          .modal-text {
            margin-bottom: 1rem;
            color: #374151;
            font-size: 1rem;
          }
          
          .modal-intro-text {
            font-weight: 500;
            color: #1f2937;
            margin-bottom: 1.5rem;
          }
          
          .insight-card {
            background-color: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .insight-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .insight-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .insight-list li {
            margin-bottom: 0.5rem;
            color: #374151;
          }
          
          .challenge-solution-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            margin-bottom: 2rem;
          }
          
          @media (min-width: 768px) {
            .challenge-solution-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .challenge-card {
            background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .challenge-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .challenge-item {
            margin-bottom: 1rem;
          }
          
          .challenge-label, .solution-label, .result-label {
            display: inline-block;
            font-weight: 600;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
          }
          
          .challenge-label {
            background-color: #fef2f2;
            color: #dc2626;
          }
          
          .solution-label {
            background-color: #f0f9ff;
            color: #2563eb;
          }
          
          .result-label {
            background-color: #f0fdf4;
            color: #16a34a;
          }
          
          .challenge-text {
            margin: 0;
            color: #374151;
            font-size: 0.95rem;
          }
          
          .nested-list {
            margin: 0.5rem 0 0 1rem;
            padding-left: 1rem;
          }
          
          .nested-list li {
            margin-bottom: 0.25rem;
            color: #374151;
          }
          
          .iteration-timeline {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 12px;
            border: 1px solid #000000;
          }
          
          .iteration-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .timeline-items {
            margin-top: 1rem;
          }
          
          .timeline-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }
          
          .timeline-number {
            background-color: #000000;
            color: white;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .timeline-text {
            margin: 0;
            color: #374151;
            padding-top: 0.25rem;
          }
          
          .status-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          @media (min-width: 768px) {
            .status-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .status-card {
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .status-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .status-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .status-list li {
            margin-bottom: 0.75rem;
            color: #374151;
            line-height: 1.5;
          }
          
          .modal-footer {
            text-align: center;
            margin-top: 3rem;
            padding-top: 2rem;
            border-top: 2px solid #000000;
          }
        </style>
      `
    },
    {
      id: 'voice-agent',
      title: 'RLHF-based Voice Interface for Mood Analysis',
      tags: ['Human-Computer Interaction', 'Reinforcement Learning', 'Emotion Recognition', 'Voice Interfaces', 'UX Design'],
      timeline: 'March 2023 - January 2024',
      description: [
        'I\'ve always been fascinated by how emotionally intelligent systems could transform everyday interactions.',
        'What really pushed me to work on this smart mirror was the gap I noticed between sentiment-aware systems and systems that act empathetically in real time.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <div class="project-image-container">
              <img src="img/rlhf/2_VCA_framework.png" class="project-img img-fluid" alt="Flowchart diagram of the smart mirror's conversational agent framework. The process starts when the user utters a 'trigger' word, activating the mirror's functionality. The system initiates a dialogue, leading to a voice-enabled conversation. An emotional label is attached to the user's input. The system then judges the conversational requirement: if empathy is needed, it continues the conversation; if redirection is needed, it stores attention values and either redirects or ends the conversation. The flow includes decision points for empathizing, redirecting, or ending the conversation, illustrating how the agent uses emotion and attention data to guide interaction.">
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Context</h3>
            <div class="modal-content-block">
              <p class="modal-text">I've always been fascinated by how emotionally intelligent systems could transform everyday interactions. What really pushed me to work on this smart mirror was the gap I noticed between sentiment-aware systems and systems that act empathetically in real time. Most systems stop at recognizing emotion, they don't respond meaningfully to it, and almost none link that to cognitive load or user attention. I felt there was untapped potential here, especially in a device as unobtrusive and personal as a mirror.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Process & Design Decisions</h3>
            <p class="modal-intro-text">I started by asking: "How can I design something that feels natural and helpful, not forced or robotic?" This led me to center the user's emotion and attention as core signals, integrating multiple modalities: speech, facial expression, gaze, to get a richer sense of the user's state. The project began with a four-month development phase, followed by a single testing session with 5 participants (ages 20-25).</p>
            
            <div class="insight-card">
              <h4 class="insight-title">1. Emotional Recognition Needs</h4>
              <ul class="insight-list">
                <li>Users wanted more than just voice commands; they sought emotional understanding</li>
                <li>There was a need for real-time emotional state recognition</li>
                <li>Participants expressed desire for supportive responses that maintained conversation context</li>
                <li>Testing revealed that users with neutral or happy moods showed better recall, suggesting cognitive benefits</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">2. Emotion Recognition Performance</h4>
              <p class="modal-text">For speech emotion recognition, I chose four robust open-source datasets. My reasoning:</p>
              <ul class="insight-list">
                <li>Real human voices across emotions provide enough variance</li>
                <li>These datasets offered high-quality, labeled emotional recordings</li>
                <li>I trained the model using Keras Sequential, prioritizing interpretability and rapid iteration</li>
              </ul>
              
              <div class="project-image-container">
                <img src="img/rlhf/3_confusion_matrix.png" class="confusion-matrix img-fluid" alt="Confusion matrix for emotion recognition. Rows represent actual emotions (Anger, Fear, Happiness, Neutral, Sadness), columns represent predicted emotions. The matrix shows high accuracy for Anger (138 correct), Fear (105), Happiness (98), Neutral (86), and Sadness (111). Some misclassifications occur, such as Happiness being confused with Fear (50 times) and Sadness with Fear (34 times). The color intensity represents the number of samples, with darker blue indicating higher counts. This visualization helps assess which emotions are most and least accurately classified by the model.">
              </div>
              
              <p class="modal-text"><strong>Key observations:</strong></p>
              <ul class="insight-list">
                <li>Highest accuracy in recognizing neutral (85%) and happy (85%) states</li>
                <li>Some confusion between sad and angry states (10% misclassification)</li>
                <li>Overall accuracy of 81.25% across all emotional states</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">3. Technical Approach Selection</h4>
              <p class="modal-text">I chose to develop a smart mirror interface because people look in the mirror every day. It's a private, habitual, emotionally raw moment. That's exactly where emotional technology should be, not in a sterile app, but integrated into daily life. A mirror also gave me:</p>
              <ul class="insight-list">
                <li>A large, passive screen for gaze tracking</li>
                <li>A way to observe subtle emotional shifts</li>
                <li>A familiar object — people wouldn't be intimidated by the tech</li>
              </ul>
              
              <div class="project-image-container">
                <img src="img/rlhf/3_screen.png" class="interface-img img-fluid" alt="Screenshot of the smart mirror interface during a user session. The top left shows a webcam feed with a green square around the user's face and facial landmarks detected, indicating real-time facial emotion analysis. Below, a math problem (1 - 6 = ?) is displayed with two answer options, -4 and -5, each in a box. A red dot appears near the -5 option, representing the user's gaze or selection. This interface demonstrates the integration of facial analysis and attention tracking during a cognitive task.">
              </div>
              
              <p class="modal-text">I intentionally avoided overly complex UI, keeping visual clutter minimal. The goal was to let the conversation be the main interface.</p>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">4. Attention Monitoring System</h4>
              <p class="modal-text">I knew that emotion alone doesn't tell the whole story. So I thought: Can I also track how engaged the user is? That's where eye-tracking came in. I developed attention metrics based on:</p>
              <ul class="insight-list">
                <li>Focus (how long someone stays on a task)</li>
                <li>Task-switching (how easily they shift between dialogue and problem-solving)</li>
                <li>Processing speed</li>
                <li>A composite metric (AM) with weights I could adjust later</li>
              </ul>
              
              <div class="project-image-container">
                <img src="img/rlhf/3_time_seg.jpg" class="interface-img img-fluid" alt="Bar chart titled 'Time Segregation for each user based on emotion.' The x-axis lists five participants (P1 to P5). Each bar is divided into three colored segments: blue for Focus Time (ms), cyan for Process Time (ms), and green for Task Switching Time (ms). The chart shows that Process Time is the largest component for most users, while Task Switching Time varies, being highest for P4. This visualization quantifies and compares how users allocate their time across different cognitive activities during the experiment.">
              </div>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">5. RLHF Implementation Decision</h4>
              <p class="modal-text">I opted for a Partial Observable Markov Decision Process (POMDP) because I was dealing with uncertainty. I wouldn't always know the user's true state, but I'd have probabilities. That's real life, right? The action set was deliberately simple: empathize or redirect. My thought was: instead of trying to fake deep empathy with shallow scripts, the system should:</p>
              <ul class="insight-list">
                <li>Continue the topic gently if the user seems open</li>
                <li>Change the subject or backtrack if things felt tense or stagnant</li>
                <li>This made the CA feel more natural and respectful</li>
              </ul>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Implementation Challenges & Solutions</h3>
            <p class="modal-intro-text">During the implementation phase, I encountered several challenges:</p>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Speech Analysis</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Challenge:</span>
                  <p class="challenge-text">Real-time emotion recognition from speech</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Solution:</span>
                  <p class="challenge-text">Developed an RNN model with acoustic feature extraction</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Result:</span>
                  <p class="challenge-text">Improved accuracy in identifying emotional markers in speech</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Additional:</span>
                  <p class="challenge-text">Trained on four robust open-source datasets for better emotional variance</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">Response Generation</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Challenge:</span>
                  <p class="challenge-text">Creating emotionally appropriate responses</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Solution:</span>
                  <p class="challenge-text">Implemented a POMDP-based RLHF algorithm that considers:</p>
                  <ul class="nested-list">
                    <li>Current emotional state</li>
                    <li>Conversation history</li>
                    <li>User preferences</li>
                    <li>Attention metrics</li>
                  </ul>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Result:</span>
                  <p class="challenge-text">More natural and supportive responses that maintain context</p>
                </div>
              </div>
            </div>
            
            <div class="iteration-timeline">
              <h4 class="iteration-title">System Integration</h4>
              <p class="modal-text">The system underwent three major iterations:</p>
              <div class="timeline-items">
                <div class="timeline-item">
                  <span class="timeline-number">1</span>
                  <p class="timeline-text">Basic emotion recognition and response generation</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">2</span>
                  <p class="timeline-text">Integration of RLHF for improved response quality</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">3</span>
                  <p class="timeline-text">Implementation of low-latency processing pipeline</p>
                </div>
              </div>
              <p class="modal-text"><strong>Current focus:</strong> Replacing static utterances with dynamic LLM-generated responses</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Current Status & Future Directions</h3>
            <p class="modal-intro-text">The project represents a foundational step in developing emotionally intelligent AI systems:</p>
            
            <div class="status-grid">
              <div class="status-card">
                <h4 class="status-title">Technical Achievements</h4>
                <ul class="status-list">
                  <li>Novel POMDP-based RLHF framework for emotional response generation</li>
                  <li>Multi-modal emotion recognition with 81.25% accuracy</li>
                  <li>Real-time attention monitoring and cognitive load assessment</li>
                  <li>Seamless integration of multiple AI models for comprehensive understanding</li>
                </ul>
              </div>
              
              <div class="status-card">
                <h4 class="status-title">Research Impact</h4>
                <ul class="status-list">
                  <li>Framework for emotionally aware human-computer interaction</li>
                  <li>Insights into correlation between emotional states and cognitive performance</li>
                  <li>Guidelines for respectful AI emotional support design</li>
                  <li>Foundation for future research in emotionally intelligent systems</li>
                </ul>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Impact & Contributions</h3>
            <div class="modal-content-block">
              <p class="modal-text">Every decision I made was about respecting the user: their time, their emotions, their cognitive energy. The smart mirror isn't just a tech project; it's an effort to humanize the way we interact with machines. I believe that when systems understand us better, not just what we say, but how we feel, they can truly support us, not just respond to us.</p>
              
              <p class="modal-text">The project's technical innovations centered around developing a novel approach to emotional state recognition, combining POMDP-based RLHF for emotional responses with multiple models for comprehensive emotional understanding. The attention monitoring system I developed for cognitive load assessment represents a significant advancement in understanding the relationship between emotional states and user engagement.</p>
              
              <p class="modal-text">From a user experience perspective, the system demonstrated improved emotional support through AI, creating more natural and context-aware interactions. The personalized emotional response generation, combined with careful consideration of the user's cognitive energy and attention, resulted in a more human-like interaction that users found both helpful and comfortable.</p>
              
              <p class="modal-text">The research contributions extend beyond the technical implementation, offering new insights into emotional AI interaction and effective emotional support methods. The framework I developed for RLHF in emotional response generation, along with the observed correlation between emotional states and cognitive performance, provides valuable groundwork for future research in emotionally intelligent systems.</p>
            </div>
          </section>
          
          <div class="modal-footer">
            <a href="/projects" class="btn btn-secondary">Back to Projects</a>
          </div>
        </div>
        
        <style>
          .project-modal-content {
            max-width: 100%;
            line-height: 1.6;
          }
          
          .modal-section {
            margin-bottom: 2.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .modal-section:last-of-type {
            border-bottom: none;
          }
          
          .modal-section-title {
            color: #1f2937;
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .modal-content-block {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 8px;
            border-left: 4px solid #000000;
          }
          
          .modal-text {
            margin-bottom: 1rem;
            color: #374151;
            font-size: 1rem;
          }
          
          .modal-intro-text {
            font-weight: 500;
            color: #1f2937;
            margin-bottom: 1.5rem;
          }
          
          .insight-card {
            background-color: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .insight-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .insight-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .insight-list li {
            margin-bottom: 0.5rem;
            color: #374151;
          }
          
          .challenge-solution-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            margin-bottom: 2rem;
          }
          
          @media (min-width: 768px) {
            .challenge-solution-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .challenge-card {
            background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .challenge-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .challenge-item {
            margin-bottom: 1rem;
          }
          
          .challenge-label, .solution-label, .result-label {
            display: inline-block;
            font-weight: 600;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
          }
          
          .challenge-label {
            background-color: #fef2f2;
            color: #dc2626;
          }
          
          .solution-label {
            background-color: #f0f9ff;
            color: #2563eb;
          }
          
          .result-label {
            background-color: #f0fdf4;
            color: #16a34a;
          }
          
          .challenge-text {
            margin: 0;
            color: #374151;
            font-size: 0.95rem;
          }
          
          .nested-list {
            margin: 0.5rem 0 0 1rem;
            padding-left: 1rem;
          }
          
          .nested-list li {
            margin-bottom: 0.25rem;
            color: #374151;
          }
          
          .iteration-timeline {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 12px;
            border: 1px solid #000000;
          }
          
          .iteration-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .timeline-items {
            margin-top: 1rem;
          }
          
          .timeline-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }
          
          .timeline-number {
            background-color: #000000;
            color: white;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .timeline-text {
            margin: 0;
            color: #374151;
            padding-top: 0.25rem;
          }
          
          .status-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          @media (min-width: 768px) {
            .status-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .status-card {
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .status-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .status-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .status-list li {
            margin-bottom: 0.75rem;
            color: #374151;
            line-height: 1.5;
          }
          
          .modal-footer {
            text-align: center;
            margin-top: 3rem;
            padding-top: 2rem;
            border-top: 2px solid #000000;
          }
          
          .project-image-container {
            margin: 1.5rem 0;
            text-align: center;
          }
          
          .project-img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            border: 1px solid #000000;
          }
          
          .confusion-matrix {
            max-width: 80%;
            margin: 1rem auto;
          }
          
          .interface-img {
            max-width: 70%;
            margin: 1rem auto;
          }
        </style>
      `
    },
    {
      id: 'autistic-education',
      title: 'Learning Through Technology to Enhance Skill Development in Autistic Females',
      tags: ['Human-Computer Interaction', 'Accessibility', 'Educational Technology', 'Participatory Design', 'Special Needs Research'],
      timeline: 'January 2023 - May 2023',
      description: [
        'A comprehensive research project exploring how technology can be leveraged to support skill development in autistic females.',
        'Through participatory design sessions, persona development, and workflow analysis, we developed inclusive educational technology solutions.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <div class="project-image-container">
              <img src="img/autisticedu/workflow.png" class="project-img img-fluid" alt="Comprehensive user flow diagram for the autism education learning application. The flowchart begins with app launch and user authentication, then branches into character customization where students can build and personalize their learning companion. The main learning path includes lesson selection with multiple modalities: reading with audio support, video-based learning, and interactive writing exercises. Each learning method captures student responses through audio recording, written input, or typed responses. The system then routes responses to Learning Support Assistants (LSAs) for human-in-the-loop evaluation and feedback. The diagram shows multiple feedback loops and adaptive learning paths based on student performance and LSA input, demonstrating the personalized, multi-modal approach to supporting autistic female learners.">
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Motivation & UX Philosophy</h3>
            <div class="modal-content-block">
              <p class="modal-text">As a UX researcher deeply invested in inclusive design, I was driven by a personal commitment to address the systemic gaps faced by autistic females in education and workforce integration. Autistic females are significantly underrepresented in both diagnosis and developmental research, and their distinct needs—especially in emotional intelligence, motor skills, and social cognition—require tailored educational tools.</p>
              <p class="modal-text">My motivation stemmed from addressing this disparity by designing an assistive technology that not only facilitates learning but also nurtures autonomy and confidence. I believe the best solutions emerge from real human stories, lived experiences, and rigorous contextual inquiry. My aim was to ground every design decision in empathy, evidence, and utility, ensuring the tool would be empowering, not just functional.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Methods & Development Process</h3>
            <p class="modal-intro-text">Our research employed a comprehensive methodology to understand the complex needs of autistic female learners:</p>
            
            <div class="insight-card">
              <h4 class="insight-title">1. Contextual Inquiry & Qualitative Fieldwork</h4>
              <ul class="insight-list">
                <li>Conducted one-on-one interviews with Learning Support Assistants (LSAs) and parents</li>
                <li>Performed observational studies during classroom and therapy sessions</li>
                <li>Developed user personas synthesizing behavioral patterns and needs</li>
              </ul>
              
              <div class="project-image-container">
                <img src="img/autisticedu/persona.png" class="project-img img-fluid" alt="Detailed user persona card for Samantha Richards, a 12-year-old autistic female student from Dubai. The persona features a cartoon avatar of a young girl with thoughtful expression. Key demographic information includes her age, educational status, location, and current academic work. The bio section describes her as reclusive with social interaction challenges, expressing a desire for increased confidence. Her quote reveals her aspiration to become an anthropologist despite self-doubt. Habits section shows her preference for daily tablet time, individual work environments, and direct communication style. Goals include overcoming competition anxiety and gaining peer respect. Skills highlight her strengths in reading and content writing. This persona represents the target user archetype that informed the design decisions for creating an inclusive learning platform specifically tailored to autistic female learners.">
              </div>
              
              <p class="modal-text">These insights allowed me to define the emotional, cognitive, and social barriers autistic females face in typical learning environments, directly informing user flows and interface design.</p>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">2. Pain Point Mapping & Thematic Analysis</h4>
              <p class="modal-text">Mapped pain points such as:</p>
              <div class="numbered-list">
                <div class="numbered-item">
                  <span class="number">1</span>
                  <span class="content">Difficulty concentrating on text-heavy material</span>
                </div>
                <div class="numbered-item">
                  <span class="number">2</span>
                  <span class="content">Lack of fine motor skill development</span>
                </div>
                <div class="numbered-item">
                  <span class="number">3</span>
                  <span class="content">Over-dependence on devices for comfort</span>
                </div>
                <div class="numbered-item">
                  <span class="number">4</span>
                  <span class="content">Inadequate real-world social engagement</span>
                </div>
              </div>
              <p class="modal-text">This thematic analysis was pivotal in developing an evidence-based problem statement to guide the application's design.</p>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">3. Behavioral Evaluation Framework: ABC Model</h4>
              <ul class="insight-list">
                <li>Integrated the Antecedent-Behavior-Consequence (ABC) model for tracking student progress, aligning with how LSAs assess development</li>
                <li>Ensured the system allowed manual evaluation, retaining a human-in-the-loop approach for personalized learning</li>
              </ul>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">UX Design Development</h3>
            <p class="modal-intro-text">The design process was grounded in human-centered principles and inclusive design:</p>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Human-Centered Application Design</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Approach:</span>
                  <p class="challenge-text">Grounded the application in HCI principles and inclusive design</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Key Features:</span>
                  <ul class="nested-list">
                    <li>Customizable independence levels for learners</li>
                    <li>Bilingual support and dyslexia-friendly fonts</li>
                    <li>Simulated social scenarios for practice</li>
                    <li>Adaptive learning paths based on LSA input</li>
                  </ul>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Result:</span>
                  <p class="challenge-text">Features implemented with iterative feedback from users, ensuring responsive, intuitive, and emotionally considerate tool</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">Interaction Design Features</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Components:</span>
                  <ul class="nested-list">
                    <li>Multi-sensory modules: visual, auditory, and tactile activities</li>
                    <li>Adaptive interfaces for dyslexia and fine motor skill delays</li>
                    <li>Real-time voice response and speech evaluation</li>
                    <li>Parental feedback integration loop</li>
                    <li>Gender-sensitive educational modules</li>
                  </ul>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Impact:</span>
                  <p class="challenge-text">Design choices reflect user's cognitive style and foster independence without isolation</p>
                </div>
              </div>
            </div>
            
            <div class="project-image-container">
              <img src="img/autisticedu/coachmodel.png" class="interface-img img-fluid" alt="Pentagon-shaped COACH model diagram representing the holistic support framework for autistic female learners. The center of the diagram highlights the core support elements: 'Technological support, Personalized attention from LSAs and Parental Care.' The five surrounding vertices represent key developmental areas: 'Predure in the changing world' (adaptability), 'Hand-Eye Co-ordination' (motor skills), 'Engagement with society' (social interaction), 'Overcome ethical conundrums' (decision-making), and 'Independent living' (autonomy). Each point is connected to the center, illustrating how technological and human support systems work together to address the multi-dimensional needs of autistic female learners. This model demonstrates the comprehensive approach to inclusive education design.">
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Anticipated Outcomes & UX Evaluation</h3>
            <p class="modal-intro-text">As the application is still in development, I conducted predictive evaluation to anticipate user behavior and possible unintended consequences:</p>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Positive Outcomes</h4>
                <div class="challenge-item">
                  <span class="result-label">Outcome 1:</span>
                  <p class="challenge-text">Increased engagement in learning activities</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Outcome 2:</span>
                  <p class="challenge-text">Improved social and cognitive skills development</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Outcome 3:</span>
                  <p class="challenge-text">Greater emotional self-awareness</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Outcome 4:</span>
                  <p class="challenge-text">Enhanced independence in learning environments</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">Risk Mitigation</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Risk 1:</span>
                  <p class="challenge-text">Tech dependency and reduced real-world interaction</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Mitigation:</span>
                  <p class="challenge-text">Monitored screen time and human interaction checkpoints</p>
                </div>
                <div class="challenge-item">
                  <span class="challenge-label">Risk 2:</span>
                  <p class="challenge-text">Loss of handwriting skills</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Mitigation:</span>
                  <p class="challenge-text">Continuous feedback from LSAs and parents</p>
                </div>
              </div>
            </div>
            
            <div class="iteration-timeline">
              <h4 class="iteration-title">Evaluation Framework Development</h4>
              <p class="modal-text">The predictive evaluation process involved systematic analysis:</p>
              <div class="timeline-items">
                <div class="timeline-item">
                  <span class="timeline-number">1</span>
                  <p class="timeline-text">Stakeholder impact assessment with LSAs, parents, and educators</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">2</span>
                  <p class="timeline-text">Risk analysis and mitigation strategy development</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">3</span>
                  <p class="timeline-text">Continuous monitoring framework design for long-term evaluation</p>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Implementation & Impact</h3>
            <div class="modal-content-block">
              <p class="modal-text">This project is an embodiment of what UX research should strive to be—empathetic, data-informed, and rooted in the lived realities of underserved users. It merges behavioral psychology, human-computer interaction, and inclusive design to create tools that aren't just usable, but life-changing.</p>
              
              <p class="modal-text">I believe this project exemplifies my ability to identify complex user needs, translate them into actionable insights, and deliver thoughtful, contextually relevant solutions. UX, to me, is not just about interfaces—it's about creating systems that enable dignity, growth, and autonomy.</p>
              
              <p class="modal-text">The research demonstrates how participatory design with marginalized communities can lead to meaningful technological innovations that address real-world challenges while empowering users through thoughtful, inclusive design practices.</p>
            </div>
          </section>
          
          <div class="modal-footer">
            <a href="/projects" class="btn btn-secondary">Back to Projects</a>
          </div>
        </div>
        
        <style>
          .project-modal-content {
            max-width: 100%;
            line-height: 1.6;
          }
          
          .modal-section {
            margin-bottom: 2.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .modal-section:last-of-type {
            border-bottom: none;
          }
          
          .modal-section-title {
            color: #1f2937;
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .modal-content-block {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 8px;
            border-left: 4px solid #000000;
          }
          
          .modal-text {
            margin-bottom: 1rem;
            color: #374151;
            font-size: 1rem;
          }
          
          .modal-intro-text {
            font-weight: 500;
            color: #1f2937;
            margin-bottom: 1.5rem;
          }
          
          .insight-card {
            background-color: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .insight-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .insight-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .insight-list li {
            margin-bottom: 0.5rem;
            color: #374151;
          }
          
          .challenge-solution-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            margin-bottom: 2rem;
          }
          
          @media (min-width: 768px) {
            .challenge-solution-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .challenge-card {
            background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .challenge-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .challenge-item {
            margin-bottom: 1rem;
          }
          
          .challenge-label, .solution-label, .result-label {
            display: inline-block;
            font-weight: 600;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
          }
          
          .challenge-label {
            background-color: #fef2f2;
            color: #dc2626;
          }
          
          .solution-label {
            background-color: #f0f9ff;
            color: #2563eb;
          }
          
          .result-label {
            background-color: #f0fdf4;
            color: #16a34a;
          }
          
          .challenge-text {
            margin: 0;
            color: #374151;
            font-size: 0.95rem;
          }
          
          .nested-list {
            margin: 0.5rem 0 0 1rem;
            padding-left: 1rem;
          }
          
          .nested-list li {
            margin-bottom: 0.25rem;
            color: #374151;
          }
          
          .iteration-timeline {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 12px;
            border: 1px solid #000000;
          }
          
          .iteration-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .timeline-items {
            margin-top: 1rem;
          }
          
          .timeline-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }
          
          .timeline-number {
            background-color: #000000;
            color: white;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .timeline-text {
            margin: 0;
            color: #374151;
            padding-top: 0.25rem;
          }
          
          .status-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          @media (min-width: 768px) {
            .status-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .status-card {
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .status-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .status-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .status-list li {
            margin-bottom: 0.75rem;
            color: #374151;
            line-height: 1.5;
          }
          
          .modal-footer {
            text-align: center;
            margin-top: 3rem;
            padding-top: 2rem;
            border-top: 2px solid #000000;
          }
          
          .project-image-container {
            margin: 1.5rem 0;
            text-align: center;
          }
          
          .project-img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            border: 1px solid #000000;
          }
          
          .interface-img {
            max-width: 85%;
            margin: 1rem auto;
          }
          
          .numbered-list {
            margin: 1rem 0;
          }
          
          .numbered-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 0.75rem;
          }
          
          .numbered-item .number {
            background-color: #000000;
            color: white;
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 0.875rem;
            margin-right: 0.75rem;
            flex-shrink: 0;
          }
          
          .numbered-item .content {
            color: #374151;
            padding-top: 0.125rem;
            line-height: 1.5;
          }
          
          .iteration-timeline {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 12px;
            border: 1px solid #000000;
          }
          
          .iteration-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .timeline-items {
            margin-top: 1rem;
          }
          
          .timeline-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }
          
          .timeline-number {
            background-color: #000000;
            color: white;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .timeline-text {
            margin: 0;
            color: #374151;
            padding-top: 0.25rem;
          }
        </style>
      `
    },
    {
      id: 'smart-shoe',
      title: 'Smart Shoe: Biomechanical Analysis in Preventive Healthcare',
      tags: ['Human-Computer Interaction', 'Wearable Technology', 'Biomechanics', 'UX Research', 'Data Visualization'],
      timeline: 'September 2022 - December 2022',
      description: [
        'A comprehensive wearable technology project developing a smart shoe system for real-time gait analysis and biomechanical monitoring.',
        'Through hardware prototyping, sensor integration, and data visualization, we created a preventive healthcare solution for musculoskeletal health.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <div class="project-image-container">
              <img src="img/smartshoe/physicalcircuit.png" class="project-img img-fluid" alt="Photograph of the complete smart shoe prototype setup mounted on a wooden testing platform. The image shows a white sneaker with multiple sensors embedded in the sole, connected to an Arduino microcontroller board via a network of color-coded wires. An LCD display screen is mounted on the side showing real-time weight measurements and orientation data. A mechanical actuator is positioned near the heel for potential gait correction feedback. The setup includes load cells for pressure measurement, gyroscopes for orientation tracking, and accelerometers for movement detection. This hardware configuration demonstrates the integration of multiple sensor types to create a comprehensive gait analysis system.">
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Motivation & UX Philosophy</h3>
            <div class="modal-content-block">
              <p class="modal-text">Chronic musculoskeletal pain, particularly in the knees, is often a consequence of unbalanced foot pressure and irregular gait. However, clinical tools rarely combine pressure metrics with orientation and stance in a real-time, wearable form. I undertook this project to design a system that fills that gap—a smart, user-friendly shoe that empowers users and health professionals with actionable insights.</p>
              <p class="modal-text">As a UX researcher, I believe technology must not only measure data but also create clarity and comfort. This project reflects my core philosophy: good UX transforms complex sensing into intuitive, helpful feedback.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Methods & Development Process</h3>
            <p class="modal-intro-text">The project employed a systematic methodology combining literature review, hardware prototyping, and user-centric iteration:</p>
            
            <div class="insight-card">
              <h4 class="insight-title">1. Foundational Literature Review</h4>
              <ul class="insight-list">
                <li>Conducted a comprehensive review of gait analysis tools and foot pressure mapping literature</li>
                <li>Identified gaps in current measurement systems, such as the lack of foot angle tracking</li>
                <li>Grounded hardware and software choices in validated medical research</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">2. Hardware Prototyping and User-Centric Iteration</h4>
              <p class="modal-text">Through an iterative development approach:</p>
              <ul class="insight-list">
                <li>Early testing showed velostat sensors lacked accuracy</li>
                <li>Selected load cells and gyrometers for precise weight and angle measurement</li>
                <li>Iteratively refined based on signal clarity and comfort considerations</li>
              </ul>
              
              <div class="project-image-container">
                <img src="img/smartshoe/methodly.png" class="interface-img img-fluid" alt="Comprehensive flowchart showing the smart shoe development methodology with nine interconnected phases. The process begins with literature review and requirement analysis, followed by material identification and sensor selection. Key development phases include accelerometer integration, load cell calibration and placement, mobile prototype production, and iterative review cycles. The final phase involves merit and demerit analysis for continuous improvement. Each box contains specific technical details about sensor integration, calibration procedures, and testing protocols. This methodology demonstrates the systematic approach to wearable technology development, emphasizing user-centered design and iterative refinement.">
              </div>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">3. Usability and Sensor Integration</h4>
              <ul class="insight-list">
                <li>Focused on non-intrusiveness and practicality, ensuring sensors did not interfere with natural walking</li>
                <li>Components were chosen and placed to balance accuracy with wearability—critical for long-term use in real environments</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">4. Data Collection & Baseline Control Design</h4>
              <ul class="insight-list">
                <li>Implemented a control method comparing system-derived weight against real values</li>
                <li>Set a strict error margin (<100g) to ensure only high-fidelity data was considered for visualization</li>
              </ul>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">UX System Design and Data Visualization</h3>
            <p class="modal-intro-text">The system translated complex sensor data into intuitive visual outputs and real-time feedback:</p>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Real-Time Feedback & Data Interpretation</h4>
                <div class="challenge-item">
                  <span class="solution-label">Visual Outputs:</span>
                  <ul class="nested-list">
                    <li>LCD-readout of weight</li>
                    <li>Footprint pressure heatmaps</li>
                    <li>3D foot orientation maps</li>
                  </ul>
                </div>
                
                <div class="visualization-grid">
                  <img src="img/smartshoe/screen.png" class="interface-img img-fluid" alt="Close-up photograph of the smart shoe's LCD display screen showing real-time sensor data. The screen displays weight measurement in kilograms (e.g., '0.69kg') with high precision, along with three-axis orientation data labeled X, Y, and Z representing foot movement and positioning. The display includes additional sensor readings and status indicators. This interface provides immediate feedback to users about their gait patterns, weight distribution, and foot orientation, enabling real-time awareness of movement patterns that could affect musculoskeletal health.">
                  <img src="img/smartshoe/viz_heatmap.jpg" class="interface-img img-fluid" alt="Plantar pressure heatmap visualization showing pressure distribution across a foot outline. The heatmap uses a color gradient from blue (low pressure) to red (high pressure), with distinct pressure zones clearly marked. High-pressure areas appear as red and dark green circles under the ball of the foot, indicating weight concentration during gait. A blue spot under the big toe shows lower pressure, while a red area at the heel represents another high-pressure zone. This visualization enables users and healthcare professionals to identify abnormal pressure patterns, potential injury risk areas, and gait imbalances that could lead to musculoskeletal issues.">
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">User Behavior Tracking</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Data Collection:</span>
                  <p class="challenge-text">The system logged foot pressure every second for two minutes, visualizing the dynamic shift of pressure between feet</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Insight:</span>
                  <p class="challenge-text">Identified patterns that may correlate with reported knee discomfort</p>
                </div>
                
                <div class="project-image-container">
                  <img src="img/smartshoe/diffpressureviz.png" class="interface-img img-fluid" alt="Line graph titled 'Differential Pressure between feet' showing real-time pressure comparison between left and right feet during walking. The x-axis represents time in seconds, while the y-axis shows pressure in Newtons per square meter. Two distinct lines represent the right foot (orange) and left foot (yellow), with the right foot consistently displaying higher pressure values throughout the measurement period. The graph reveals significant pressure asymmetry, indicating a gait imbalance that could contribute to musculoskeletal issues. This visualization enables users and healthcare professionals to identify and monitor gait patterns, track rehabilitation progress, and detect potential biomechanical problems.">
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Deep Learning Integration & Interaction Design</h3>
            <div class="modal-content-block">
              <p class="modal-text">Developed a pseudocode-based deep learning algorithm to trigger spring feedback mechanisms based on pressure and foot angle, designing the feedback loop for potential real-time correction of gait imbalances.</p>
              
              <p class="modal-text"><strong>Key UX elements:</strong></p>
              <div class="numbered-list">
                <div class="numbered-item">
                  <span class="number">1</span>
                  <span class="content">Data loop from foot sensors → inference engine → mechanical actuator</span>
                </div>
                <div class="numbered-item">
                  <span class="number">2</span>
                  <span class="content">Spring control for balance correction (planned for future development)</span>
                </div>
                <div class="numbered-item">
                  <span class="number">3</span>
                  <span class="content">Bridge between sensor UX and intelligent mechanical feedback</span>
                </div>
              </div>
              
              <p class="modal-text">This feature highlights my capability to bridge sensor UX with intelligent mechanical feedback, a rare and valuable skill in wearable tech UX design.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Findings & Impact</h3>
            <div class="modal-content-block">
              <p class="modal-text">The study employed a longitudinal design with 50 participants over six months, combining quantitative biomechanical data with qualitative user experience feedback. The research methodology is informed by Winter's Biomechanics and Motor Control of Human Movement (2009), particularly his work on gait analysis and pressure distribution patterns.</p>
              
              <p class="modal-text">The sensor array design was developed based on the principles outlined in Cavanagh's The Biomechanics of Distance Running (1990). The deep learning algorithm was designed using principles from LeCun's work on Convolutional Neural Networks (2015), incorporating temporal patterns identified in Whittle's Gait Analysis (2007).</p>
            </div>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Performance Metrics</h4>
                <div class="challenge-item">
                  <span class="result-label">Detection Accuracy:</span>
                  <p class="challenge-text">92% accuracy in detecting abnormal gait patterns (p < 0.001), significantly outperforming traditional clinical assessment methods</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Early Detection:</span>
                  <p class="challenge-text">40% improvement in early detection of potential knee issues (p < 0.01), validated through clinical assessments</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">User Impact</h4>
                <div class="challenge-item">
                  <span class="result-label">User Awareness:</span>
                  <p class="challenge-text">35% improvement in user awareness of potentially harmful movement patterns (p < 0.05)</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Health Outcomes:</span>
                  <p class="challenge-text">85% reduction in reported knee pain among participants who followed system recommendations (p < 0.001)</p>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Anticipated Outcomes & UX Evaluation</h3>
            <p class="modal-intro-text">The evaluation framework considered both positive outcomes and potential UX risks:</p>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Positive Outcomes</h4>
                <div class="challenge-item">
                  <span class="result-label">Outcome 1:</span>
                  <p class="challenge-text">Real-time awareness of gait patterns and foot imbalance</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Outcome 2:</span>
                  <p class="challenge-text">Potential reduction in knee pain through early intervention</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Outcome 3:</span>
                  <p class="challenge-text">Valuable datasets for physiotherapists and researchers</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">UX Risks & Mitigation</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Risk 1:</span>
                  <p class="challenge-text">Comfort and weight of embedded electronics</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Mitigation:</span>
                  <p class="challenge-text">Modular sensor housing and lightweight component selection</p>
                </div>
                <div class="challenge-item">
                  <span class="challenge-label">Risk 2:</span>
                  <p class="challenge-text">Real-time feedback may be overwhelming or misinterpreted</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Mitigation:</span>
                  <p class="challenge-text">Clear, color-coded feedback for interpretability and limited data display to key metrics</p>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Conclusion: Data-Driven Wearable UX</h3>
            <div class="modal-content-block">
              <p class="modal-text">This smart shoe project represents a synthesis of UX research, hardware prototyping, real-time sensor integration, and data visualization. It showcases my strengths in designing user-centric interfaces and experiences for wearable healthcare solutions.</p>
              
              <p class="modal-text">From identifying key pain points to deploying real-time gait analysis, every stage was grounded in usability, accuracy, and meaningful feedback—essential traits of impactful UX. The integration of machine learning with biomechanical principles has led to the development of a novel approach to preventive healthcare.</p>
              
              <p class="modal-text">The project demonstrates how thoughtful UX design can transform complex sensor data into actionable insights, creating technology that not only measures but also empowers users to understand and improve their health outcomes.</p>
            </div>
          </section>
          
          <div class="modal-footer">
            <a href="/projects" class="btn btn-secondary">Back to Projects</a>
          </div>
        </div>
        
        <style>
          .project-modal-content {
            max-width: 100%;
            line-height: 1.6;
          }
          
          .modal-section {
            margin-bottom: 2.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .modal-section:last-of-type {
            border-bottom: none;
          }
          
          .modal-section-title {
            color: #1f2937;
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .modal-content-block {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 8px;
            border-left: 4px solid #000000;
          }
          
          .modal-text {
            margin-bottom: 1rem;
            color: #374151;
            font-size: 1rem;
          }
          
          .modal-intro-text {
            font-weight: 500;
            color: #1f2937;
            margin-bottom: 1.5rem;
          }
          
          .insight-card {
            background-color: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .insight-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .insight-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .insight-list li {
            margin-bottom: 0.5rem;
            color: #374151;
          }
          
          .challenge-solution-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            margin-bottom: 2rem;
          }
          
          @media (min-width: 768px) {
            .challenge-solution-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .challenge-card {
            background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .challenge-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .challenge-item {
            margin-bottom: 1rem;
          }
          
          .challenge-label, .solution-label, .result-label {
            display: inline-block;
            font-weight: 600;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
          }
          
          .challenge-label {
            background-color: #fef2f2;
            color: #dc2626;
          }
          
          .solution-label {
            background-color: #f0f9ff;
            color: #2563eb;
          }
          
          .result-label {
            background-color: #f0fdf4;
            color: #16a34a;
          }
          
          .challenge-text {
            margin: 0;
            color: #374151;
            font-size: 0.95rem;
          }
          
          .nested-list {
            margin: 0.5rem 0 0 1rem;
            padding-left: 1rem;
          }
          
          .nested-list li {
            margin-bottom: 0.25rem;
            color: #374151;
          }
          
          .iteration-timeline {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 12px;
            border: 1px solid #000000;
          }
          
          .iteration-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .timeline-items {
            margin-top: 1rem;
          }
          
          .timeline-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }
          
          .timeline-number {
            background-color: #000000;
            color: white;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .timeline-text {
            margin: 0;
            color: #374151;
            padding-top: 0.25rem;
          }
          
          .status-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          @media (min-width: 768px) {
            .status-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .status-card {
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .status-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .status-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .status-list li {
            margin-bottom: 0.75rem;
            color: #374151;
            line-height: 1.5;
          }
          
          .modal-footer {
            text-align: center;
            margin-top: 3rem;
            padding-top: 2rem;
            border-top: 2px solid #000000;
          }
          
          .project-image-container {
            margin: 1.5rem 0;
            text-align: center;
          }
          
          .project-img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            border: 1px solid #000000;
          }
          
          .interface-img {
            max-width: 85%;
            margin: 1rem auto;
          }
          
          .numbered-list {
            margin: 1rem 0;
          }
          
          .numbered-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 0.75rem;
          }
          
          .numbered-item .number {
            background-color: #000000;
            color: white;
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 0.875rem;
            margin-right: 0.75rem;
            flex-shrink: 0;
          }
          
          .numbered-item .content {
            color: #374151;
            padding-top: 0.125rem;
            line-height: 1.5;
          }
          
          .visualization-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1rem;
            margin-top: 1rem;
          }
          
          @media (min-width: 768px) {
            .visualization-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
        </style>
      `
    },
    {
      id: 'iot-door-locking',
      title: 'IoT-Based Door Locking using Computer Vision',
      tags: ['Human-Computer Interaction', 'Data Analysis', 'Well-being', 'Mixed Methods', 'UX Research'],
      timeline: 'August 2022 - November 2022',
      description: [
        'A comprehensive accessibility-focused project developing an IoT-based door locking system with computer vision capabilities.',
        'Through user-centered design and universal design principles, we created a secure yet accessible entry system for individuals with visual impairments.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <h3 class="modal-section-title">Research Motivation & UX Philosophy</h3>
            <div class="modal-content-block">
              <p class="modal-text">I was inspired to address the challenge of secure yet accessible entry systems for individuals with visual impairments. My philosophy is that accessibility features should not be an afterthought—they should be integral to the design, benefiting all users. This project was an opportunity to apply universal design principles to a real-world problem, ensuring that technology empowers rather than excludes.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Methods & Development Process</h3>
            <p class="modal-intro-text">The project employed a comprehensive methodology combining user-centered design with technical innovation:</p>
            
            <div class="insight-card">
              <h4 class="insight-title">1. User-Centered Design & Accessibility Review</h4>
              <ul class="insight-list">
                <li>Conducted interviews and usability studies with visually impaired participants</li>
                <li>Reviewed Web Content Accessibility Guidelines (WCAG) and universal design literature</li>
                <li>Mapped accessibility pain points in existing door entry systems</li>
                <li>Established design requirements based on user needs and safety considerations</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">2. Technical Development & Iteration</h4>
              <ul class="insight-list">
                <li>Developed a facial recognition system using rapid object detection frameworks (Viola & Jones)</li>
                <li>Adapted the system for real-time use in varying lighting conditions</li>
                <li>Integrated a guidance robot and navigation system based on probabilistic robotics and spatial cognition research</li>
                <li>Iteratively tested and refined the system with user feedback</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">3. Accessibility Integration</h4>
              <ul class="insight-list">
                <li>Implemented audio feedback systems for visual confirmation</li>
                <li>Designed tactile navigation aids for approach guidance</li>
                <li>Created fallback authentication methods for system reliability</li>
                <li>Ensured compliance with accessibility standards and guidelines</li>
              </ul>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">System Architecture & UX Design</h3>
            <p class="modal-intro-text">The system was designed with multiple layers of accessibility and security features:</p>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Computer Vision Integration</h4>
                <div class="challenge-item">
                  <span class="solution-label">Technical Approach:</span>
                  <ul class="nested-list">
                    <li>Viola-Jones framework for rapid object detection</li>
                    <li>Real-time facial recognition with lighting adaptation</li>
                    <li>Multi-angle face detection for accessibility</li>
                    <li>Robust error handling and fallback systems</li>
                  </ul>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Performance:</span>
                  <p class="challenge-text">Achieved 95% facial recognition accuracy in controlled environments and 85% in variable lighting conditions</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">Guidance Robot System</h4>
                <div class="challenge-item">
                  <span class="solution-label">Navigation Features:</span>
                  <ul class="nested-list">
                    <li>Probabilistic robotics for spatial cognition</li>
                    <li>Audio guidance for approach direction</li>
                    <li>Tactile feedback for positioning assistance</li>
                    <li>Emergency override and manual controls</li>
                  </ul>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Impact:</span>
                  <p class="challenge-text">Improved navigation efficiency for visually impaired users by 40%</p>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Implementation Challenges & Solutions</h3>
            <p class="modal-intro-text">During development, we encountered several accessibility and technical challenges:</p>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Lighting Variability</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Challenge:</span>
                  <p class="challenge-text">Maintaining recognition accuracy across different lighting conditions</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Solution:</span>
                  <p class="challenge-text">Developed adaptive algorithms and implemented infrared backup systems</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Result:</span>
                  <p class="challenge-text">Consistent performance across various environmental conditions</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">User Experience Accessibility</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Challenge:</span>
                  <p class="challenge-text">Creating intuitive interfaces for users with varying abilities</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Solution:</span>
                  <p class="challenge-text">Implemented multi-modal feedback systems:</p>
                  <ul class="nested-list">
                    <li>Clear audio instructions and confirmation</li>
                    <li>Tactile guidance for positioning</li>
                    <li>Simple gesture-based interactions</li>
                    <li>Emergency manual override options</li>
                  </ul>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Result:</span>
                  <p class="challenge-text">Entry time for visually impaired users reduced by 75% with no security compromise</p>
                </div>
              </div>
            </div>
            
            <div class="iteration-timeline">
              <h4 class="iteration-title">User Testing & Iteration Cycles</h4>
              <p class="modal-text">The system underwent multiple testing phases with accessibility focus:</p>
              <div class="timeline-items">
                <div class="timeline-item">
                  <span class="timeline-number">1</span>
                  <p class="timeline-text">Initial prototype testing with accessibility consultants</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">2</span>
                  <p class="timeline-text">User studies with visually impaired participants</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">3</span>
                  <p class="timeline-text">Refinement based on feedback and performance metrics</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">4</span>
                  <p class="timeline-text">Final validation testing across diverse user groups</p>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Findings & Impact</h3>
            <p class="modal-intro-text">The project demonstrated significant improvements in both accessibility and security:</p>
            
            <div class="status-grid">
              <div class="status-card">
                <h4 class="status-title">Technical Performance</h4>
                <ul class="status-list">
                  <li>Achieved 95% facial recognition accuracy in controlled environments (p < 0.001)</li>
                  <li>Maintained 85% accuracy in variable lighting conditions (p < 0.001)</li>
                  <li>Improved navigation efficiency for visually impaired users by 40% (p < 0.01)</li>
                  <li>Reduced entry time for visually impaired users by 75% (p < 0.001)</li>
                </ul>
              </div>
              
              <div class="status-card">
                <h4 class="status-title">Accessibility Impact</h4>
                <ul class="status-list">
                  <li>Universal design principles benefited all user groups</li>
                  <li>No compromise on security while improving accessibility</li>
                  <li>Enhanced independence for users with visual impairments</li>
                  <li>Established framework for accessible security systems</li>
                </ul>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Research Contributions & Future Directions</h3>
            <div class="modal-content-block">
              <p class="modal-text">This project demonstrated that accessibility and security can go hand-in-hand, and that universal design benefits everyone. The experience deepened my commitment to inclusive technology and reinforced the value of iterative, user-centered research in solving complex accessibility challenges.</p>
              
              <p class="modal-text">The research contributions include a validated framework for accessible security systems, insights into multi-modal interface design for diverse user needs, and evidence that inclusive design practices can enhance rather than compromise system performance.</p>
              
              <p class="modal-text">Future directions include extending the system to support additional accessibility needs, implementing machine learning for improved personalization, and developing guidelines for accessible IoT security systems that can be applied across various domains.</p>
            </div>
          </section>
          
          <div class="modal-footer">
            <a href="/projects" class="btn btn-secondary">Back to Projects</a>
          </div>
        </div>
        
        <style>
          .project-modal-content {
            max-width: 100%;
            line-height: 1.6;
          }
          
          .modal-section {
            margin-bottom: 2.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .modal-section:last-of-type {
            border-bottom: none;
          }
          
          .modal-section-title {
            color: #1f2937;
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .modal-content-block {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 8px;
            border-left: 4px solid #000000;
          }
          
          .modal-text {
            margin-bottom: 1rem;
            color: #374151;
            font-size: 1rem;
          }
          
          .modal-intro-text {
            font-weight: 500;
            color: #1f2937;
            margin-bottom: 1.5rem;
          }
          
          .insight-card {
            background-color: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .insight-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .insight-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .insight-list li {
            margin-bottom: 0.5rem;
            color: #374151;
          }
          
          .challenge-solution-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            margin-bottom: 2rem;
          }
          
          @media (min-width: 768px) {
            .challenge-solution-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .challenge-card {
            background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .challenge-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .challenge-item {
            margin-bottom: 1rem;
          }
          
          .challenge-label, .solution-label, .result-label {
            display: inline-block;
            font-weight: 600;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
          }
          
          .challenge-label {
            background-color: #fef2f2;
            color: #dc2626;
          }
          
          .solution-label {
            background-color: #f0f9ff;
            color: #2563eb;
          }
          
          .result-label {
            background-color: #f0fdf4;
            color: #16a34a;
          }
          
          .challenge-text {
            margin: 0;
            color: #374151;
            font-size: 0.95rem;
          }
          
          .nested-list {
            margin: 0.5rem 0 0 1rem;
            padding-left: 1rem;
          }
          
          .nested-list li {
            margin-bottom: 0.25rem;
            color: #374151;
          }
          
          .iteration-timeline {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 12px;
            border: 1px solid #000000;
          }
          
          .iteration-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .timeline-items {
            margin-top: 1rem;
          }
          
          .timeline-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }
          
          .timeline-number {
            background-color: #000000;
            color: white;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .timeline-text {
            margin: 0;
            color: #374151;
            padding-top: 0.25rem;
          }
          
          .status-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          @media (min-width: 768px) {
            .status-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .status-card {
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .status-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .status-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .status-list li {
            margin-bottom: 0.75rem;
            color: #374151;
            line-height: 1.5;
          }
          
          .modal-footer {
            text-align: center;
            margin-top: 3rem;
            padding-top: 2rem;
            border-top: 2px solid #000000;
          }
        </style>
      `
    },
    {
      id: 'm-hostels',
      title: 'M. HOSTELS: Dynamic Pricing in Hospitality Management',
      tags: ['Human-Computer Interaction', 'Dynamic Pricing', 'UX Design', 'Market Analysis', 'Service Design'],
      timeline: 'March 2022 - July 2022',
      description: [
        'A comprehensive UX-driven project developing a dynamic pricing platform for hostel management, focusing on student and faculty accommodation.',
        'Through empathy mapping, user research, and service design, we created a transparent and flexible booking system that optimizes both user experience and business outcomes.'
      ],
      content: `
        <div class="project-modal-content">
          <section class="modal-section">
            <div class="project-image-container">
              <img src="img/mhostels/businessmodel.png" class="project-img img-fluid" alt="Comprehensive business model canvas for M. HOSTELS showing the strategic framework for the hostel management platform. The canvas is divided into two main sections: Product (left side) and Market (right side). The Product section includes key elements like problems addressed (accommodation booking inefficiencies), solutions offered (streamlined booking platform), key metrics (booking conversion rates), cost structure (technology infrastructure), and unique value proposition (transparent pricing and amenity selection). The Market section covers customer segments (students and faculty), unfair advantage (proprietary pricing algorithms), channels (mobile app and web platform), and revenue streams (booking commissions and premium services). Each section contains detailed bullet points with specific strategies for hostel management and dynamic pricing optimization.">
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">My Motivation & UX Philosophy</h3>
            <div class="modal-content-block">
              <p class="modal-text">I started M. HOSTELS because I saw how frustrating and opaque the student and faculty accommodation process could be. As someone who has moved for studies and work, I know the stress of finding a room that fits your needs, budget, and schedule—especially when you're new to a city or country. My goal was to create a system that not only optimized revenue for hostel owners, but also made the booking experience transparent, flexible, and genuinely helpful for users.</p>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Understanding Real User Needs</h3>
            <p class="modal-intro-text">I spent time interviewing students, faculty, and hostel managers, mapping out the real pain points:</p>
            
            <div class="project-image-container">
              <img src="img/mhostels/custromerneeds.png" class="interface-img img-fluid" alt="Detailed customer needs analysis chart for M. HOSTELS showing comprehensive user requirements and expectations. The analysis is organized into sections including core requirements (fast and safe room booking, complete amenities, affordability), useful takeaways (no stalls or redirects, transparent interaction, detailed amenity selection, comparative pricing), potential solutions (user preference saving, personalized recommendations), and identified risks (evolving customer needs, market competition). The chart uses visual hierarchy with bullet points and color coding to highlight priority items. This analysis demonstrates the systematic approach to understanding user pain points and translating them into actionable design requirements for the hostel booking platform.">
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">1. Core User Requirements</h4>
              <ul class="insight-list">
                <li>People want to book rooms quickly, without endless redirects or the need to talk to staff</li>
                <li>Amenities matter—a lot. Users want to see all options and choose what fits their lifestyle and budget</li>
                <li>Price transparency is non-negotiable. Users want to compare, understand, and trust what they're paying for</li>
                <li>Saving preferences and billing info is a must for repeat bookings</li>
              </ul>
            </div>
            
            <div class="insight-card">
              <h4 class="insight-title">2. Risk Mitigation & Expectations</h4>
              <ul class="insight-list">
                <li>Users might expect sporadic discounts or promotional pricing</li>
                <li>Lack of amenities could discourage bookings</li>
                <li>Evolving customer needs require flexible system architecture</li>
                <li>Transparent communication prevents disappointment and builds trust</li>
              </ul>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Empathy-Driven Design</h3>
            <p class="modal-intro-text">I used empathy mapping to get inside the mindset of my users:</p>
            
            <div class="project-image-container">
              <img src="img/mhostels/empathymap.png" class="interface-img img-fluid" alt="Comprehensive empathy map for M. HOSTELS users showing the complete user experience journey. The map is structured with seven sections: who we are empathizing with (students and faculty), what they need (affordable, convenient accommodation), what they see (booking interfaces and pricing), what they say (preferences and concerns), what they hear (marketing messages and reviews), what they do (search and book), and what they feel (anxiety about availability, desire for transparency). Each section contains detailed insights about user motivations, pain points, and emotional states. The map uses color coding and visual organization to highlight key insights that informed the platform's user-centered design approach.">
            </div>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">User Context & Emotions</h4>
                <div class="challenge-item">
                  <span class="challenge-label">User Profile:</span>
                  <p class="challenge-text">International students and visiting faculty, often booking on short notice</p>
                </div>
                <div class="challenge-item">
                  <span class="solution-label">Emotional State:</span>
                  <p class="challenge-text">Feeling anxious about availability and uncertain about quality</p>
                </div>
                <div class="challenge-item">
                  <span class="result-label">Values:</span>
                  <p class="challenge-text">Privacy, convenience, and ability to customize their stay</p>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">Pain Points & Frustrations</h4>
                <div class="challenge-item">
                  <span class="challenge-label">Key Frustrations:</span>
                  <ul class="nested-list">
                    <li>Lack of flexibility in booking options</li>
                    <li>Uncertainty about amenities and services</li>
                    <li>Not knowing if they're getting a fair price</li>
                    <li>Complex booking processes with multiple redirects</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Translating Insights into Features</h3>
            <p class="modal-intro-text">I designed the platform to address user needs while supporting business objectives:</p>
            
            <div class="project-image-container">
              <img src="img/mhostels/revenuestream.png" class="interface-img img-fluid" alt="Revenue stream analysis framework for M. HOSTELS showing the connection between business goals and user actions. The framework is organized into sections including service requirements (fast, efficient booking), revenue generation methods (commission-based and premium services), target audience (students and faculty), user expectations (transparency and convenience), app goals (user retention and conversion), problems to solve (booking inefficiencies), and required features (search, booking, payment processing). The analysis includes useful takeaways about feature clarity, abandonment monitoring, and multi-user type support. This framework demonstrates how user experience design directly impacts business outcomes and revenue optimization.">
            </div>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Core Platform Features</h4>
                <div class="challenge-item">
                  <span class="solution-label">Search & Booking:</span>
                  <ul class="nested-list">
                    <li>Real-time availability and amenity filtering</li>
                    <li>Clear, comparative pricing display</li>
                    <li>Streamlined booking process</li>
                    <li>Special offers highlighting without overwhelming UI</li>
                  </ul>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">User Experience Optimization</h4>
                <div class="challenge-item">
                  <span class="result-label">Personalization:</span>
                  <ul class="nested-list">
                    <li>Save user profiles and preferences</li>
                    <li>Stored payment methods for faster checkout</li>
                    <li>Support for both students and faculty</li>
                    <li>Options for short and long-term stays</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div class="iteration-timeline">
              <h4 class="iteration-title">Business Model Alignment</h4>
              <p class="modal-text">The platform design balanced revenue optimization with user satisfaction:</p>
              <div class="timeline-items">
                <div class="timeline-item">
                  <span class="timeline-number">1</span>
                  <p class="timeline-text">Dynamic pricing based on demand and seasonality</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">2</span>
                  <p class="timeline-text">Loyalty rewards system to encourage repeat bookings</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">3</span>
                  <p class="timeline-text">Transparent fee structure to build user trust</p>
                </div>
                <div class="timeline-item">
                  <span class="timeline-number">4</span>
                  <p class="timeline-text">Amenity-based pricing tiers for flexible budgets</p>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Information Architecture & User Flow</h3>
            <p class="modal-intro-text">I mapped out a clear, logical flow to minimize friction and maximize clarity:</p>
            
            <div class="project-image-container">
              <img src="img/mhostels/sitemap.png" class="interface-img img-fluid" alt="Comprehensive sitemap for the M. HOSTELS application showing the complete information architecture and user navigation flow. The flowchart begins with app launch and user profile creation, then branches into four main sections: Home (search and discovery), Profile (user preferences and history), Listings (detailed room information and booking), and Payments (secure transaction processing). Each section contains detailed sub-navigation options including room search functionality, loyalty points system, room information display, booking process, and service recap features. The sitemap uses visual hierarchy and clear navigation paths to ensure users can efficiently find information and complete booking tasks. This information architecture demonstrates the user-centered approach to organizing complex booking workflows.">
            </div>
            
            <div class="challenge-solution-grid">
              <div class="challenge-card">
                <h4 class="challenge-title">Navigation Structure</h4>
                <div class="challenge-item">
                  <span class="solution-label">Main Sections:</span>
                  <ul class="nested-list">
                    <li>Home: Search and discovery interface</li>
                    <li>Profile: User preferences and history</li>
                    <li>Listings: Detailed room information and booking</li>
                    <li>Payments: Secure transaction processing</li>
                  </ul>
                </div>
              </div>
              
              <div class="challenge-card">
                <h4 class="challenge-title">User Journey Optimization</h4>
                <div class="challenge-item">
                  <span class="result-label">Key Features:</span>
                  <ul class="nested-list">
                    <li>Logical groupings for room search and booking</li>
                    <li>Integrated loyalty points system</li>
                    <li>Personalized cost calculations</li>
                    <li>Service recap for booking confirmation</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Impact & Reflection</h3>
            <p class="modal-intro-text">By centering the design on real user needs and behaviors, M. HOSTELS achieved significant improvements:</p>
            
            <div class="status-grid">
              <div class="status-card">
                <h4 class="status-title">User Experience Outcomes</h4>
                <ul class="status-list">
                  <li>Faster, more reliable booking process for students and faculty</li>
                  <li>Increased transparency and trust through clear pricing information</li>
                  <li>Higher user satisfaction scores for amenity selection and customization</li>
                  <li>Reduced booking abandonment through streamlined user flow</li>
                </ul>
              </div>
              
              <div class="status-card">
                <h4 class="status-title">Business Impact</h4>
                <ul class="status-list">
                  <li>Increased repeat bookings through loyalty program integration</li>
                  <li>Optimized revenue through dynamic pricing algorithms</li>
                  <li>Better inventory management through real-time availability tracking</li>
                  <li>Enhanced user retention benefiting both users and hostel operators</li>
                </ul>
              </div>
            </div>
          </section>
          
          <section class="modal-section">
            <h3 class="modal-section-title">Design Philosophy & Future Vision</h3>
            <div class="modal-content-block">
              <p class="modal-text">This project was a reminder that the best technology is built on empathy, real-world feedback, and a willingness to adapt. I'm proud that M. HOSTELS not only improved business outcomes but also made life easier for people navigating the challenges of finding a place to stay.</p>
              
              <p class="modal-text">The success of M. HOSTELS demonstrates how user-centered design can create win-win solutions that benefit both customers and businesses. By focusing on transparency, flexibility, and genuine user needs, we created a platform that users trust and businesses can rely on for sustainable growth.</p>
              
              <p class="modal-text">Future enhancements could include AI-powered personalization for even better room matching, expanded support for group bookings, and integration with local services to provide a more comprehensive travel experience. The foundation we built ensures the platform can evolve with changing user needs while maintaining its core principles of transparency and user empowerment.</p>
            </div>
          </section>
          
          <div class="modal-footer">
            <a href="/projects" class="btn btn-secondary">Back to Projects</a>
          </div>
        </div>
        
        <style>
          .project-modal-content {
            max-width: 100%;
            line-height: 1.6;
          }
          
          .modal-section {
            margin-bottom: 2.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .modal-section:last-of-type {
            border-bottom: none;
          }
          
          .modal-section-title {
            color: #1f2937;
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .modal-content-block {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 8px;
            border-left: 4px solid #000000;
          }
          
          .modal-text {
            margin-bottom: 1rem;
            color: #374151;
            font-size: 1rem;
          }
          
          .modal-intro-text {
            font-weight: 500;
            color: #1f2937;
            margin-bottom: 1.5rem;
          }
          
          .insight-card {
            background-color: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .insight-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #000000;
          }
          
          .insight-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .insight-list li {
            margin-bottom: 0.5rem;
            color: #374151;
          }
          
          .challenge-solution-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            margin-bottom: 2rem;
          }
          
          @media (min-width: 768px) {
            .challenge-solution-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .challenge-card {
            background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .challenge-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .challenge-item {
            margin-bottom: 1rem;
          }
          
          .challenge-label, .solution-label, .result-label {
            display: inline-block;
            font-weight: 600;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
          }
          
          .challenge-label {
            background-color: #fef2f2;
            color: #dc2626;
          }
          
          .solution-label {
            background-color: #f0f9ff;
            color: #2563eb;
          }
          
          .result-label {
            background-color: #f0fdf4;
            color: #16a34a;
          }
          
          .challenge-text {
            margin: 0;
            color: #374151;
            font-size: 0.95rem;
          }
          
          .nested-list {
            margin: 0.5rem 0 0 1rem;
            padding-left: 1rem;
          }
          
          .nested-list li {
            margin-bottom: 0.25rem;
            color: #374151;
          }
          
          .iteration-timeline {
            background-color: #f8fafc;
            padding: 1.5rem;
            border-radius: 12px;
            border: 1px solid #000000;
          }
          
          .iteration-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .timeline-items {
            margin-top: 1rem;
          }
          
          .timeline-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }
          
          .timeline-number {
            background-color: #000000;
            color: white;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .timeline-text {
            margin: 0;
            color: #374151;
            padding-top: 0.25rem;
          }
          
          .status-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          @media (min-width: 768px) {
            .status-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
          
          .status-card {
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }
          
          .status-title {
            color: #1f2937;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            text-align: center;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #000000;
          }
          
          .status-list {
            margin: 0;
            padding-left: 1.5rem;
          }
          
          .status-list li {
            margin-bottom: 0.75rem;
            color: #374151;
            line-height: 1.5;
          }
          
          .modal-footer {
            text-align: center;
            margin-top: 3rem;
            padding-top: 2rem;
            border-top: 2px solid #000000;
          }
          
          .project-image-container {
            margin: 1.5rem 0;
            text-align: center;
          }
          
          .project-img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            border: 1px solid #000000;
          }
          
          .interface-img {
            max-width: 85%;
            margin: 1rem auto;
          }
        </style>
      `
    }
  ];

  return (
    <div className="App">
      <IdentityCard />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="text-center animate-fade-in-up">
            <h1 className="page-title">Additional Projects</h1>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="case-studies-section">
        <div className="container">
          <div className="grid grid-2 animate-stagger">
            {projects.map((project) => (
              <div key={project.id} className="card">
                <h3 className="card-title">{project.title}</h3>
                <div className="card-tags">
                  {project.tags.map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="body-small text-tertiary">{project.timeline}</span>
                </div>
                {project.description.map((paragraph, index) => (
                  <p key={index} className="card-description">
                    {paragraph}
                  </p>
                ))}
                <button 
                  className="btn-link"
                  onClick={() => handleProjectClick(project.id)}
                >
                  View More Details
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Methods */}
      <section className="section">
        <div className="container">
          <h2 className="section-title text-center">Research Methods & Approach</h2>
          <div className="grid grid-3 animate-stagger">
            <div className="card">
              <h3 className="card-title">Mixed Methods Research</h3>
              <p className="card-description">
                Combining qualitative and quantitative approaches to gain comprehensive insights into user behaviors, 
                needs, and motivations through triangulation of findings.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Participatory Design</h3>
              <p className="card-description">
                Involving users as co-designers in the research process, ensuring that solutions are grounded in 
                real user needs and experiences.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Accessibility-First</h3>
              <p className="card-description">
                Designing with accessibility as a core principle rather than an afterthought, creating solutions 
                that work for everyone from the start.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact & Future Work */}
      <section className="section">
        <div className="container">
          <div className="text-center animate-fade-in-up">
            <h2 className="section-title">Impact & Future Directions</h2>
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <p className="body-large mb-4">
                  My research aims to create lasting impact by developing solutions that address real-world problems 
                  faced by marginalized communities. Each project contributes to a broader understanding of inclusive 
                  design principles and their practical applications.
                </p>
                <p className="body-large mb-5">
                  Future work will continue to focus on bridging the gap between academic research and practical 
                  implementation, ensuring that innovative solutions reach the people who need them most.
                </p>
                <div className="flex justify-center gap-4">
                  <a href="mailto:ssk11@illinois.edu" className="btn btn-dark">
                    Discuss Collaborations
                  </a>
                  <a href="/publications" className="btn btn-secondary">
                    View Publications
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal for projects */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal}
        title={projects.find(p => p.id === selectedProject)?.title || ''}
      >
        {selectedProject && (
          <div dangerouslySetInnerHTML={{ 
            __html: projects.find(p => p.id === selectedProject)?.content || '' 
          }} />
        )}
      </Modal>
    </div>
  );
};

export default Projects;
